/**
 * The other ways into a paid rave, beside buying it once (the user,
 * 2026-10-07, evening; the paywall's shape 2026-10-08):
 *
 *  - **spend a pack credit** or **one of this year's tier raves** — only by
 *    pressing the button ("Use one of my raves" — never silently); when there
 *    is one, it is the paywall's PRIMARY action and buying goes second;
 *  - **"See deals and plans"** — the pack of three and the yearly plans live
 *    on `app.…/plans`, not on the paywall (the user, 2026-10-08: "buy this
 *    rave for $5, or browse the buying deals and subscriptions").
 *
 * And in the phone app, the purchase itself goes through the App Store or
 * Google Play (`lib/store.ts`) — the stores' rules for digital goods — so
 * there the Stripe form is never shown and codes are not offered (the stores
 * have their own offer codes).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatUsd, isRavePriceProductId, productById, type AnyProductId, type EntitlementSummary } from '@myrave/shared';
import { api, ApiError } from '@/lib/api';
import { ENTITLEMENTS_KEY, spendable, useEntitlements } from '@/lib/entitlements';
import { buyInStore, storePrices } from '@/lib/store';
import { appUrl } from '@/lib/tenant';
import { Button, Card, Stack, Txt } from '@/ui';
import { useT } from '@/i18n';
import { CardForm } from './Paywall';
import './RaveOptions.css';
import { LogoImg } from './Logo';
import { useCatalogue, usePurchasesOn } from '@/lib/catalogue';
import { isFramed } from '@/lib/framed';

export { ENTITLEMENTS_KEY, useEntitlements } from '@/lib/entitlements';

/** The stores' own prices, by store product id — shown instead of ours in the app. */
export function useStorePrices(enabled: boolean) {
  return useQuery({ queryKey: ['store', 'prices'], queryFn: storePrices, enabled, staleTime: 10 * 60_000 });
}

/** Does the account hold something that opens a rave without paying again? */
export function useHasSpendable(enabled = true): boolean {
  const { tierRaves, credits } = spendable(useEntitlements(enabled).data);
  return tierRaves > 0 || credits > 0;
}

/**
 * Spend a credit or a tier rave on this edition — the person's explicit
 * choice. The first of them is the PRIMARY button: holding one is the reason
 * not to pay again. Nothing at all when there is nothing to spend.
 */
export function SpendOptions({ editionId, onGranted }: { editionId: string; onGranted: () => void }) {
  const tr = useT();
  const queryClient = useQueryClient();
  const held = useEntitlements();
  const [error, setError] = useState<string | null>(null);
  const redeem = useMutation({
    mutationFn: (using: 'credit' | 'tier') => api.post<{ status: string; entitlements: EntitlementSummary }>(`/editions/${editionId}/redeem`, { using }),
    onSuccess: (res) => {
      if (res?.entitlements) queryClient.setQueryData(ENTITLEMENTS_KEY, res.entitlements);
      onGranted();
    },
    onError: (e: Error) => setError(e instanceof ApiError ? e.message : tr('That did not go through. Nothing was spent.')),
  });
  const e = held.data;
  const { tierRaves, credits } = spendable(e);
  if (!e || (credits <= 0 && tierRaves <= 0)) return null;
  return (
    <Stack gap={8}>
      {tierRaves > 0 && e.tier && (
        <Button variant="primary" full disabled={redeem.isPending} onClick={() => { setError(null); redeem.mutate('tier'); }}>
          {tr('Use one of my raves — {count} left this year on {tier}', { count: tierRaves, tier: e.tier.tier })}
        </Button>
      )}
      {credits > 0 && (
        <Button variant={tierRaves > 0 ? 'secondary' : 'primary'} full disabled={redeem.isPending} onClick={() => { setError(null); redeem.mutate('credit'); }}>
          {tr('Use a rave credit — {count} left', { count: credits })}
        </Button>
      )}
      {error && <Txt as="div" role="alert" size={12.5} tone="bad">{error}</Txt>}
    </Stack>
  );
}

/**
 * What the store charges for one rave's product, and whether this phone can
 * sell it. A catalogue product is always on sale. An Independent Rave's price
 * step (the Master Rave Plans, 2026-10-13) can be bought only when the store
 * itself answered with its price: an app built before price steps, or a step
 * not yet set up in App Store Connect / Play Console, does not list it — and
 * the shell would never answer a purchase of it.
 */
export function useRaveProductPrice(product: AnyProductId, priceCents: number | null, inStore = true) {
  const cat = useCatalogue();
  const prices = useStorePrices(inStore);
  const sku = productById(product)?.store.productId;
  const listed = sku ? prices.data?.[sku] : undefined;
  const ours = product === 'RAVE_SINGLE' ? cat.priceCents.RAVE_SINGLE : priceCents ?? productById(product)?.priceCents ?? 0;
  return {
    label: listed ?? formatUsd(ours),
    sellable: !inStore || !isRavePriceProductId(product) || listed !== undefined,
    loading: inStore && prices.isLoading,
  };
}

/** In the phone app: this rave, through the store — its own product (`RAVE_SINGLE`, or its price step). */
export function StoreRaveButton({ editionId, primary, onGranted, product = 'RAVE_SINGLE', priceCents = null }: {
  editionId: string; primary: boolean; onGranted: () => void;
  product?: AnyProductId; priceCents?: number | null;
}) {
  const tr = useT();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ tone: 'bad' | 'dim'; text: string } | null>(null);
  const { label: single, sellable, loading } = useRaveProductPrice(product, priceCents);

  if (!sellable && !loading) {
    return (
      <Txt as="div" role="status" size={12.5} tone="dim">
        {tr('This rave is {price}. Update the MyRave app to buy it here.', { price: single })}
      </Txt>
    );
  }

  async function buy() {
    setBusy(true); setNote(null);
    try {
      const out = await buyInStore(product, { editionId });
      if (out.status === 'granted') {
        queryClient.setQueryData(ENTITLEMENTS_KEY, out.entitlements);
        onGranted();
      } else if (out.status === 'pending') {
        setNote({ tone: 'dim', text: tr('Your purchase is waiting for approval. The rave opens as soon as the store confirms it.') });
      } else if (out.status === 'failed') {
        setNote({ tone: 'bad', text: tr('The store did not complete the purchase. Nothing has been charged.') });
      }
    } catch {
      setNote({ tone: 'bad', text: tr('The purchase could not be checked just now. If you were charged, it opens by itself the next time the app starts.') });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack gap={8}>
      <Button variant={primary ? 'primary' : 'secondary'} full disabled={busy || loading} onClick={() => void buy()}>
        {busy ? tr('One moment…') : primary ? tr('Buy this rave — {price}', { price: single }) : tr('Buy for {price}', { price: single })}
      </Button>
      {note && <Txt as="div" role={note.tone === 'bad' ? 'alert' : 'status'} size={12.5} tone={note.tone}>{note.text}</Txt>}
    </Stack>
  );
}

/** On the web: a pack of three through Stripe's embedded Checkout (the Plans page). */
export function WebPackButton({ disabled = false }: { disabled?: boolean }) {
  const cat = useCatalogue();
  const tr = useT();
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<'idle' | 'card' | 'waiting' | 'done'>('idle');
  const [checkout, setCheckout] = useState<{ clientSecret: string; publishableKey: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [before, setBefore] = useState(0);

  const start = useMutation({
    mutationFn: () => {
      const back = new URL(window.location.href);
      return api.post<{ payment: { clientSecret?: string; status: string }; publishableKey?: string }>('/billing/checkout', { product: 'RAVE_PACK_3', returnPath: `${back.pathname}${back.search}` });
    },
    onSuccess: async (res) => {
      const held = await api.get<EntitlementSummary>('/me/entitlements').catch(() => null);
      setBefore(held?.raveCredits ?? 0);
      if (res.payment.clientSecret && res.publishableKey) {
        setCheckout({ clientSecret: res.payment.clientSecret, publishableKey: res.publishableKey });
        setPhase('card');
      } else setError(tr('That did not go through. Nothing has been charged.'));
    },
    onError: (e: Error) => setError(e instanceof ApiError ? e.message : tr('That did not go through. Nothing has been charged.')),
  });

  // The webhook grants the credits; wait for them to appear.
  useEffect(() => {
    if (phase !== 'waiting') return;
    let stop = false;
    const tick = async () => {
      const held = await api.get<EntitlementSummary>('/me/entitlements').catch(() => null);
      if (stop) return;
      if (held && held.raveCredits > before) {
        queryClient.setQueryData(ENTITLEMENTS_KEY, held);
        setPhase('done');
        return;
      }
      window.setTimeout(() => void tick(), 2000);
    };
    void tick();
    return () => { stop = true; };
  }, [phase, before, queryClient]);

  if (phase === 'card' && checkout) {
    return <CardForm clientSecret={checkout.clientSecret} publishableKey={checkout.publishableKey} onConfirmed={() => setPhase('waiting')} onCancel={() => setPhase('idle')} />;
  }
  if (phase === 'waiting') return <Txt as="div" role="status" size={12.5}>{tr('Confirming your payment… your credits appear as soon as your bank says yes.')}</Txt>;
  if (phase === 'done') return <Txt as="div" role="status" size={12.5}>{tr('Your three rave credits are ready — open any paid rave with one.')}</Txt>;
  return (
    <Stack gap={6}>
      <Button variant="primary" full disabled={disabled || start.isPending} onClick={() => { setError(null); start.mutate(); }}>
        {tr('Buy the pack of three — {price}', { price: formatUsd(cat.priceCents.RAVE_PACK_3) })}
      </Button>
      {error && <Txt as="div" role="alert" size={12.5} tone="bad">{error}</Txt>}
    </Stack>
  );
}

/**
 * The pack of three and the yearly plans — on the main app's Plans page
 * (`app.…/plans`), a real link, so it opens from a rave's host and from
 * inside the phone app alike.
 */
export function SeePlansLink({ editionId }: { editionId?: string }) {
  const tr = useT();
  const purchasesOn = usePurchasesOn() === true;
  // The rave goes along, so the Plans page can head itself with it and
  // offer it alone too (`?rave=`, read there; an id, never a URL).
  const href = appUrl(editionId ? `/plans?rave=${encodeURIComponent(editionId)}` : '/plans');
  // The main app's page: not from inside the wizard's framed preview (point 49).
  if (isFramed() || !purchasesOn) return null;
  return (
    <a className="ui-btn ui-btn--secondary is-full rave-options-plans" href={href}>
      {tr('See deals and plans')}
    </a>
  );
}

/**
 * A rave, as one strip: its logo (else its initial in a round mark), its
 * name, a line under it, and an action — the paywall's head and the Plans
 * page's "the rave you came from" (the user's Stitch design, 2026-10-08), so
 * the two screens read as one. The cover, when there is one, is a faint
 * ground behind it all.
 */
export function RaveStrip({
  name, detail, logoUrl, coverUrl, eyebrow, children, action,
}: {
  name: string;
  detail?: string | null;
  logoUrl?: string | null;
  coverUrl?: string | null;
  eyebrow?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}) {
  const [logoFailed, setLogoFailed] = useState(false);
  const [coverFailed, setCoverFailed] = useState(false);
  const logo = logoUrl && !logoFailed ? logoUrl : null;
  const initial = Array.from(name.trim())[0]?.toUpperCase() ?? '•';
  return (
    <Card className="rave-strip">
      {coverUrl && !coverFailed && <img className="rave-strip-cover" src={coverUrl} alt="" onError={() => setCoverFailed(true)} />}
      <div className="rave-strip-mark" data-logo={logo ? true : undefined} aria-hidden="true">
        {logo ? <LogoImg src={logo} alt="" onError={() => setLogoFailed(true)} /> : initial}
      </div>
      <Stack gap={4} className="rave-strip-text">
        {eyebrow}
        <Txt as="h2" className="rave-strip-name">{name}</Txt>
        {detail && <Txt as="div" size={12.5} tone="dim">{detail}</Txt>}
        {children}
      </Stack>
      {action && <div className="rave-strip-action">{action}</div>}
    </Card>
  );
}
